import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

// Same-origin `/api` — Emergent's ingress routes `/api/*` to the backend on
// every frontend host (preview, .emergent.host, custom domain), so no
// `REACT_APP_BACKEND_URL` swap is needed and CORS never applies.
const API = "/api";

export default function FoundationSign() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", data: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ status: "error", data: null, error: "Missing signature link" });
      return () => {};
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/foundation/sign`, { token });
        if (!cancelled) setState({ status: "ok", data, error: "" });
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.response?.data?.detail ||
            "We couldn't confirm this link. Please contact hello@bookaway.com.au.";
          setState({ status: "error", data: null, error: typeof msg === "string" ? msg : "Confirmation failed" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      className="min-h-screen bg-[#F7F5F2] px-6 py-24 md:px-12"
      data-testid="foundation-sign-page"
    >
      <div className="mx-auto max-w-xl rounded-3xl border border-black/10 bg-white p-8 shadow-[0_16px_60px_-30px_rgba(0,0,0,0.35)] md:p-12">
        <p className="overline mb-4">Book Away · Foundation</p>

        {state.status === "loading" && (
          <div className="flex items-center gap-3 py-8 text-[#6B6B6B]">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
            <span className="text-sm uppercase tracking-[0.28em]">
              Confirming your spot…
            </span>
          </div>
        )}

        {state.status === "ok" && (
          <div data-testid="foundation-sign-success" className="py-4">
            <CheckCircle2 className="mb-5 h-14 w-14 text-gold" />
            <h1 className="font-serif text-4xl font-light leading-tight text-[#2A2724] md:text-5xl">
              {state.data.already_signed ? "Already confirmed" : "You're in."}{" "}
              <span className="italic text-gold">
                {state.data.business_name}
              </span>
            </h1>
            <p className="mt-4 text-[#6B6B6B]">
              Your Foundation Member spot in{" "}
              <span className="text-[#2A2724]">
                {state.data.city || "Sydney"}
              </span>{" "}
              is locked in for life. The Book Away team will reach out within 24
              hours to book your concierge onboarding session.
            </p>
            <p className="mt-6 text-sm text-[#6B6B6B]">
              Any questions —{" "}
              <a
                href="mailto:hello@bookaway.com.au"
                className="text-gold hover:underline"
              >
                hello@bookaway.com.au
              </a>
            </p>
            <Link
              to="/"
              data-testid="foundation-sign-home"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-medium text-black hover:bg-[#AA8627]"
            >
              Back to Book Away →
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="foundation-sign-error" className="py-4">
            <XCircle className="mb-5 h-14 w-14 text-red-500" />
            <h1 className="font-serif text-3xl font-light text-[#2A2724]">
              We couldn't confirm this link
            </h1>
            <p className="mt-4 text-[#6B6B6B]">{state.error}</p>
            <p className="mt-6 text-sm text-[#6B6B6B]">
              Reply to your welcome email or contact{" "}
              <a
                href="mailto:hello@bookaway.com.au"
                className="text-gold hover:underline"
              >
                hello@bookaway.com.au
              </a>{" "}
              and we'll sort it in minutes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
